import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { errorMessage, useApi, useAuthHeaders } from '../api';
import { AppFooter } from '../components/AppFooter';
import { Alert, Loading } from '../components/ui';
import { useI18n } from '../i18n';
import { Markdown, type LinkResolver } from '../markdown';
import { useMe, type Me } from '../session';
import { ConsoleHeader, TenantCards } from './ConsolePage';
import { SALES_DOC_FILES, SALES_DOC_TITLES, SALES_TOOL_GROUPS, type SalesTool } from './sales-tools';

/** Platform admins and sales only: everyone else goes back to the home page (the API refuses them anyway). */
function useSeller(): { me: Me; admin: boolean } | 'loading' | 'error' | 'refused' {
  const me = useMe();
  if (me.isPending) return 'loading';
  if (me.isError) return 'error';
  const admin = me.data.staff.includes('platform_admin');
  if (!admin && !me.data.staff.includes('sales')) return 'refused';
  return { me: me.data, admin };
}

/** Staff page "Outils de vente": the demos and every sales tool, all served by Syndiqia behind the staff sign-in. */
export function SalesToolsPage() {
  const { t } = useI18n();
  const s = t.salesTools;
  const seller = useSeller();
  if (seller === 'loading') return <Loading label={t.loading} />;
  if (seller === 'error') return <Alert>{t.common.error}</Alert>;
  if (seller === 'refused') return <Navigate to="/" replace />;
  const demos = seller.me.memberships.filter((m) => m.access === 'demo');

  return (
    <div className="chooser console">
      <ConsoleHeader me={seller.me} />
      <h1>{s.title}</h1>
      <p className="muted">{s.intro}</p>

      {demos.length > 0 && (
        <section>
          <h2>{t.console.demos}</h2>
          <p className="muted small">{t.console.demosHint}</p>
          <TenantCards items={demos} badge={t.console.demoBadge} resettable />
        </section>
      )}

      {SALES_TOOL_GROUPS.map((group) => {
        const tools = group.tools.filter((tool) => seller.admin || !tool.internal);
        if (tools.length === 0) return null;
        return (
          <section key={group.title}>
            <h2>{group.title}</h2>
            <ul className="sales-tools">
              {tools.map((tool) => (
                <li key={tool.title}>
                  <ToolCard tool={tool} />
                </li>
              ))}
            </ul>
          </section>
        );
      })}
      <AppFooter />
    </div>
  );
}

function ToolCard({ tool }: { tool: SalesTool }) {
  const { t } = useI18n();
  const s = t.salesTools;
  const title = (
    <strong>
      {tool.title}
      {tool.internal && <span className="badge badge-draft sales-tool-badge">{s.internal}</span>}
    </strong>
  );
  if (tool.to)
    return (
      <Link to={tool.to} className="sales-tool">
        {title}
        <span className="muted small">{tool.description}</span>
        <span className="sales-tool-open small">{s.open}</span>
      </Link>
    );
  return (
    <div className="sales-tool">
      {title}
      <span className="muted small">{tool.description}</span>
      <span className="sales-tool-files">
        {tool.files?.map((f) => (
          <DownloadButton key={f.name} name={f.name} label={f.label} />
        ))}
      </span>
    </div>
  );
}

/** Downloads a file through the API (the request carries the sign-in, so a plain link cannot be used). */
function DownloadButton({ name, label }: { name: string; label: string }) {
  const { t } = useI18n();
  const headers = useAuthHeaders();
  const [state, setState] = useState<'idle' | 'busy' | 'error'>('idle');
  const download = async () => {
    setState('busy');
    try {
      const res = await fetch(`/api/sales/files/${encodeURIComponent(name)}`, { headers: headers() });
      if (!res.ok) throw new Error(String(res.status));
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      setState('idle');
    } catch {
      setState('error');
    }
  };
  return (
    <button type="button" className="button button-small" disabled={state === 'busy'} onClick={() => void download()}>
      {state === 'error' ? t.salesTools.downloadFailed : t.salesTools.download(label)}
    </button>
  );
}

/** A sales document (docs/sales/*.md) shown as a page. */
export function SalesDocPage() {
  const { t } = useI18n();
  const { name = '' } = useParams();
  const api = useApi();
  const seller = useSeller();
  const doc = useQuery({ queryKey: ['sales-doc', name], queryFn: () => api<{ markdown: string }>(`/api/sales/docs/${encodeURIComponent(name)}`), enabled: typeof seller === 'object' });
  if (seller === 'loading') return <Loading label={t.loading} />;
  if (seller === 'error') return <Alert>{t.common.error}</Alert>;
  if (seller === 'refused') return <Navigate to="/" replace />;

  // Links between our documents open the matching page; web links open in a new tab; anything else is plain text.
  const resolve: LinkResolver = (href) => {
    if (/^https?:\/\//.test(href)) return { href };
    const file = href.split('#')[0]!.split('/').pop() ?? '';
    if (file === 'scripts.md') return { to: '/ventes/guide' };
    if (file === 'demo-script.md') return { to: '/ventes/demo' };
    const target = SALES_DOC_FILES[file];
    return target ? { to: `/ventes/doc/${target}` } : null;
  };

  return (
    <div className="chooser console sales-doc">
      <ConsoleHeader me={seller.me} />
      <p>
        <Link to="/ventes">← {t.salesTools.title}</Link>
      </p>
      {doc.isPending && <Loading label={SALES_DOC_TITLES[name] ?? t.loading} />}
      {doc.isError && <Alert>{errorMessage(doc.error, t)}</Alert>}
      {doc.data && <Markdown source={doc.data.markdown} resolve={resolve} />}
      <AppFooter />
    </div>
  );
}
